import React, { forwardRef, useMemo } from 'react';
import type { Order, BusinessInfo, LastSentItemEntry, OrderItem } from '../../types';
import { formatLiveFishDetail, liveFishSnapshotFields } from '../../lib/liveFishPrice';

interface PrintableKitchenOrderProps {
  order: Order;
  businessInfo?: BusinessInfo;
}

const isDiscountItem = (item: OrderItem) =>
  (typeof item.price === 'number' && item.price < 0) || String(item.id).includes('combo-discount');

function toSnapshotEntry(item: OrderItem, idx: number): LastSentItemEntry {
  return {
    lineId: item.lineId ?? `pos-${idx}-${item.id}-${item.selectedMeatOption ?? ''}`,
    name: item.name,
    quantity: item.quantity,
    notes: item.notes,
    selectedMeatOption: item.selectedMeatOption,
    ...liveFishSnapshotFields(item),
  };
}

/** Snapshot of saved kitchen lines (last placed/updated cart) — baseline for the next Update print. */
export function snapshotKitchenLines(order: Pick<Order, 'items'>): LastSentItemEntry[] {
  return (order.items ?? [])
    .filter((i) => !i.removed && !isDiscountItem(i))
    .map((item, idx) => toSnapshotEntry(item, idx));
}

/**
 * Pure diff: current items vs lastSentItemSnapshot.
 * No batch/pending fallbacks — those pulled stale/wrong lines onto tickets.
 */
export function getKitchenPrintDelta(order: Order): {
  itemsToShow: OrderItem[];
  removedItems: LastSentItemEntry[];
  isAdditionsOnly: boolean;
} {
  const snapshot = order.lastSentItemSnapshot ?? [];
  const allItems = order.items ?? [];
  const items = allItems.filter((i) => !i.removed && !isDiscountItem(i));
  if (snapshot.length === 0) {
    return { itemsToShow: items, removedItems: [], isAdditionsOnly: false };
  }

  const itemsByLineId = new Map(
    allItems
      .filter((i) => !isDiscountItem(i))
      .map((i) => [i.lineId, i] as const)
      .filter(([lineId]) => !!lineId),
  );
  const removed: LastSentItemEntry[] = [];

  snapshot.forEach((s) => {
    const current = itemsByLineId.get(s.lineId);
    if (!current || current.removed) {
      removed.push(s);
    } else if (typeof current.quantity === 'number' && current.quantity < s.quantity) {
      const delta = s.quantity - current.quantity;
      if (delta > 0) removed.push({ ...s, quantity: delta });
    }
  });

  const snapshotByLineId = new Map(snapshot.map((s) => [s.lineId, s]));
  const additions: OrderItem[] = [];

  for (const item of items) {
    if (!item.lineId) {
      additions.push(item);
      continue;
    }
    const prev = snapshotByLineId.get(item.lineId);
    if (!prev) {
      additions.push(item);
    } else if (item.quantity > prev.quantity) {
      const deltaQty = item.quantity - prev.quantity;
      if (deltaQty > 0) additions.push({ ...item, quantity: deltaQty });
    }
  }

  return { itemsToShow: additions, removedItems: removed, isAdditionsOnly: true };
}

/**
 * Build a print payload whose kitchen ticket is exactly the delta of this Update:
 * baseline = cart kitchen already had; items = cart after Update.
 */
export function buildKitchenPrintOrder(
  order: Order,
  baseline: LastSentItemEntry[],
): Order {
  return {
    ...order,
    lastSentItemSnapshot: baseline,
  };
}

const PrintableKitchenOrder = forwardRef<HTMLDivElement, PrintableKitchenOrderProps>(({ order, businessInfo }, ref) => {
  const { itemsToShow, removedItems, isAdditionsOnly } = useMemo(
    () => getKitchenPrintDelta(order),
    [order.items, order.lastSentItemSnapshot],
  );

  const business = businessInfo || {
    businessName: 'KINGS PARK',
    address: '',
    gstNumber: '',
  };

  let orderIdentifier: string;
  let tableLabelUpper: string | null = null;
  if (order.orderType === 'takeaway') {
    orderIdentifier = `Takeaway #${order.takeawayNumber}`;
  } else if (order.orderType === 'delivery') {
    orderIdentifier = order.deliveryInfo
      ? `Delivery: ${order.deliveryInfo.platform} #${order.deliveryNumber}`
      : `Delivery #${order.deliveryNumber}`;
  } else {
    const rawTable = order.tableName || `Table ${order.tableNumber ?? order.tableId}`;
    orderIdentifier = rawTable;
    tableLabelUpper = rawTable.toUpperCase();
  }

  const orderDate = (() => {
    const ts = order.timestamp;
    if (typeof ts === 'number') return new Date(ts);
    if (ts && typeof ts === 'object' && 'seconds' in ts) return new Date((ts as any).seconds * 1000);
    return new Date();
  })();
  const formattedDate = isNaN(orderDate.getTime())
    ? '—'
    : orderDate.toLocaleString('en-NZ', {
        timeZone: 'Pacific/Auckland',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });

  const isRunning = order.paymentStatus !== 'paid';
  const isFirstPrint = (order.lastSentItemSnapshot ?? []).length === 0;
  const hasAdditions = itemsToShow.length > 0;
  const hasRemovals = removedItems.length > 0;
  const titleLine = isFirstPrint
    ? 'NEW ORDER'
    : hasAdditions
      ? 'RUNNING ORDER-NEW'
      : hasRemovals
        ? 'RUNNING ORDER-REMOVED'
        : isRunning
          ? 'RUNNING ORDER'
          : 'KITCHEN ORDER';

  return (
    <div ref={ref} className="printable-receipt printable-kitchen-order">
      <div className="receipt-content">
        <header className="text-center mb-3 kitchen-order-header">
          <p className="text-3xl font-extrabold uppercase tracking-wide kitchen-order-title">
            {titleLine}
          </p>
          {tableLabelUpper && (
            <p className="text-3xl font-extrabold mt-1 uppercase tracking-wide kitchen-order-table">
              {tableLabelUpper}
            </p>
          )}
        </header>
        <div className="text-xs mb-2 space-y-0.5">
          <p><strong>Order ID:</strong> {order.id}</p>
          <p>
            <strong>
              {order.orderType === 'dine-in'
                ? 'Table'
                : order.orderType === 'takeaway'
                  ? 'Takeaway'
                  : 'Delivery'}
              :
            </strong>{' '}
            {order.orderType === 'dine-in'
              ? (tableLabelUpper ?? orderIdentifier.toUpperCase())
              : orderIdentifier}
          </p>
          {(order.orderType === 'takeaway' || order.orderType === 'delivery') && order.customerName && (
            <p><strong>Customer:</strong> {order.customerName}</p>
          )}
          <p><strong>Date:</strong> {formattedDate}</p>
        </div>
        {order.notes && (
          <div className="receipt-order-notes text-xs mb-2 p-2 bg-amber-100 border-2 border-amber-400 rounded break-words">
            <p className="receipt-order-notes-label font-bold text-amber-900 uppercase">Order instructions:</p>
            <p className="font-semibold text-amber-900 break-words">{order.notes}</p>
          </div>
        )}
        <table className="w-full text-xs kitchen-order-table">
          <colgroup>
            <col style={{ width: '12%' }} />
            <col style={{ width: '88%' }} />
          </colgroup>
          <thead>
            <tr className="border-b-2 border-dashed border-black">
              <th className="text-left pb-1">QTY</th>
              <th className="text-left pb-1">ITEM</th>
            </tr>
          </thead>
          <tbody>
            {itemsToShow.length === 0 && removedItems.length === 0 ? (
              <tr>
                <td colSpan={2} className="text-gray-500 italic py-2">No items to show.</td>
              </tr>
            ) : (
              <>
                {removedItems.length > 0 &&
                  removedItems.map((entry, idx) => (
                    <tr key={entry.lineId ?? `removed-${idx}`}>
                      <td className="align-top pt-1">{entry.quantity}</td>
                      <td className="align-top pt-1 kitchen-order-item-name text-gray-500">
                        <span className="line-through">{entry.name}</span>
                        {entry.selectedMeatOption ? ` (${entry.selectedMeatOption})` : ''}
                        {formatLiveFishDetail(entry) ? ` — ${formatLiveFishDetail(entry)}` : ''}
                        <span className="ml-1 text-[10px] font-semibold text-gray-500">(REMOVED)</span>
                      </td>
                    </tr>
                  ))}
                {itemsToShow.map((item, idx) => {
                  const key = item.lineId ?? `item-${idx}-${item.id}`;
                  return (
                    <React.Fragment key={key}>
                      <tr>
                        <td className="align-top pt-1">{item.quantity}</td>
                        <td className="align-top pt-1 kitchen-order-item-name">
                          {item.name}
                          {item.selectedMeatOption ? ` (${item.selectedMeatOption})` : ''}
                          {formatLiveFishDetail(item) ? (
                            <span className="block text-[11px]">{formatLiveFishDetail(item)}</span>
                          ) : null}
                        </td>
                      </tr>
                      {item.notes && (
                        <tr className="receipt-special-instruction">
                          <td />
                          <td className="text-[11px] font-semibold text-gray-800 border-l-4 border-amber-500 bg-amber-50 pl-2 pb-1 break-words">
                            Special instructions: {item.notes}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </>
            )}
          </tbody>
        </table>
        <footer className="text-center mt-3 text-xs font-medium">
          {isAdditionsOnly ? 'Add these items to the existing order.' : '— End of kitchen order —'}
        </footer>
      </div>
    </div>
  );
});

PrintableKitchenOrder.displayName = 'PrintableKitchenOrder';

export default PrintableKitchenOrder;
